// src/pages/Login.jsx
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { loginWithPassword } from "../utils/auth";
import { convertGuestSession } from "../utils/guestConversion";
import { clearGuestConversionState } from "../utils/storage";
import Logo from "../components/brand/Logo";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import FormField, { fieldClassName } from "../components/ui/FormField";

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const shouldConvertGuest =
    location.state?.convertGuest ||
    new URLSearchParams(location.search).get("convert") === "guest";

  const finishLoginFlow = async () => {
    const converted = shouldConvertGuest
      ? await convertGuestSession(location.state)
      : false;

    if (converted) {
      clearGuestConversionState();
    }

    navigate(converted ? "/saved-shows" : "/quiz", {
      state: converted ? { convertedGuest: true } : undefined,
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setError("");
    setIsSubmitting(true);

    try {
      await loginWithPassword(email, password);
      await onLogin?.();
      await finishLoginFlow();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Login failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto grid min-h-[calc(100vh-96px)] max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_420px] lg:items-center lg:px-8">
      <div className="hidden lg:block">
        <Logo size="hero" />
        <h1 className="mt-8 font-display text-6xl font-bold leading-tight text-hookd-cream">
          Your vibe. Your next obsession.
        </h1>
        <div className="hookd-divider my-7 max-w-md">♕</div>
        <p className="max-w-xl text-lg leading-8 text-hookd-muted">
          Sign in to save recommendations, feedback, and your personal vibe profile.
        </p>
      </div>

      <Card className="p-6 sm:p-8" glow="purple">
      <h2 className="font-display text-3xl font-bold mb-4 text-hookd-cream">Sign in to Hook’d Curatress</h2>
      {shouldConvertGuest && (
        <p className="mb-4 rounded-hookd border border-hookd-gold/30 bg-hookd-gold/10 p-3 text-sm text-hookd-cream">
          Your current quiz and matches will be added to this account.
        </p>
      )}
      <form onSubmit={handleLogin} className="space-y-4">
        <FormField label="Email">
          <input
            type="email"
            placeholder="you@empress.com"
            className={fieldClassName}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </FormField>
        <FormField label="Password">
          <input
            type="password"
            placeholder="Password"
            className={fieldClassName}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </FormField>
        {error && <p className="text-sm text-red-300">{error}</p>}
        <Button
          type="submit"
          loading={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? "Logging In..." : "Log In"}
        </Button>
      </form>
      <p className="text-sm text-center mt-5 text-hookd-muted">
        New here? <Link to="/signup" state={location.state} className="inline-flex min-h-11 items-center text-hookd-empress font-semibold underline">Create an account</Link>
      </p>
      </Card>
    </div>
  );
}
